// Shared ticket list for Sentinel workspace and review queues.

import { memo, useEffect, useRef, useState } from 'react';
import { ChevronRight, PanelLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SearchInput } from '@/components/ui/input';
import { ContainedScrollArea } from '@/components/ui/contained-scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { TruncatedText } from '@/components/shared/truncated-text';
import { ViewerAvatars, type Viewer } from '@/components/shared/viewer-avatars';
import { cn, initialsOf, parseUtcDate } from '@/lib/utils';

export type TicketListVariant = 'workspace' | 'review';

/** Sentinel tickets have numeric ids; the Automation queue lists log entries by string id. */
type TicketListId = number | string;

export interface TicketListItem<Id extends TicketListId = number> {
  id: Id;
  subject: string;
  customerName: string | null;
  preview: string | null;
  category: string | null;
  timestamp: string | null;
  /** Review queue only — shown as "N pending" when above 1. */
  pendingCount?: number;
  /** Bolds the subject and shows a green badge until the ticket is opened. */
  unread?: boolean;
  /** Workspace, admin views of the auto-routed queue: who owns the ticket. */
  ownerName?: string | null;
}

export interface TicketListSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/** Cursor paging — the list observes its own sentinel and asks for the next page. */
export interface TicketListPaging {
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export interface TicketListProps<Id extends TicketListId = number> {
  items: TicketListItem<Id>[];
  selectedId: Id | null;
  onSelect: (id: Id) => void;
  isLoading: boolean;
  variant?: TicketListVariant;
  title?: React.ReactNode;
  /** Renders the "N Tickets" subtitle under the title. */
  totalCount?: number;
  /** Controls pinned to the right of the header — filters, export. */
  headerActions?: React.ReactNode;
  /** A row under the header, above the list: the Sentinel queue sections, or filters. */
  toolbar?: React.ReactNode;
  /** Renders the collapse control only when provided. */
  onCollapse?: () => void;
  search?: TicketListSearch;
  /** Sits to the right of the search box, e.g. the workspace's view filter. */
  searchAction?: React.ReactNode;
  groupByCategory?: boolean;
  paging?: TicketListPaging;
  emptyMessage?: string;
  className?: string;
  /** Workspace only — who else is on each ticket, by ticket id. Apart from `items` so a change redraws one row. */
  viewersById?: Partial<Record<Id, Viewer[]>>;
  /** Workspace only: a control shown on a row (e.g. "Assign to me"), outside the row's own button. */
  rowAction?: (item: TicketListItem<Id>) => React.ReactNode;
}

const UNCATEGORIZED = 'Uncategorized';

/** "5h ago" / "3d ago" / "1w ago" — the compact form the agent queue uses. */
function compactTimeAgo(iso: string): string {
  const date = parseUtcDate(iso);
  const diffMs = Date.now() - date.getTime();
  if (!Number.isFinite(diffMs)) return '';
  if (diffMs < 60_000) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(diffMs / 3600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/** "about 5 hours ago" — the long form the review queue uses. */
function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const date = parseUtcDate(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

export function CategoryChip({ label }: { label: string }) {
  return (
    <span className="flex h-[22px] items-center gap-2 rounded-[6px] border border-border bg-white px-3 py-1 w-fit">
      <span className="size-[6px] rounded-[1px] bg-accent-purple" />
      <span className="text-[10px] font-medium leading-[1.1] text-accent-purple">{label}</span>
    </span>
  );
}

function groupByCategoryName<Id extends TicketListId>(items: TicketListItem<Id>[]): { category: string; items: TicketListItem<Id>[] }[] {
  const groups: { category: string; items: TicketListItem<Id>[] }[] = [];
  const index = new Map<string, number>();
  for (const item of items) {
    const category = item.category ?? UNCATEGORIZED;
    const existing = index.get(category);
    if (existing === undefined) {
      index.set(category, groups.length);
      groups.push({ category, items: [item] });
    } else {
      groups[existing].items.push(item);
    }
  }
  return groups;
}

export function TicketList<Id extends TicketListId = number>({
  items,
  selectedId,
  onSelect,
  isLoading,
  variant = 'workspace',
  title = 'My tickets',
  totalCount,
  headerActions,
  toolbar,
  onCollapse,
  search,
  searchAction,
  groupByCategory = false,
  paging,
  emptyMessage,
  className,
  viewersById,
  rowAction,
}: TicketListProps<Id>) {
  // Row is memoised, which drops the generic; the id it hands back is always one of `items`.
  const selectRow = onSelect as (id: TicketListId) => void;
  const isReview = variant === 'review';
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [, refreshTime] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => refreshTime((value) => value + 1), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  // Fields, not the `paging` object: callers build it inline, so `[paging]` would re-observe every render and page forever.
  const hasMore = paging?.hasMore ?? false;
  const isFetchingNextPage = paging?.isFetchingNextPage ?? false;
  const onLoadMore = paging?.onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetchingNextPage, onLoadMore]);

  const isEmpty = !isLoading && items.length === 0;
  const showSkeleton = isLoading && (!isReview || items.length === 0);

  const rows = groupByCategory
    ? groupByCategoryName(items).map((group) => (
        <CategoryGroup key={group.category} category={group.category} count={group.items.length}>
          {group.items.map((item) => (
            <Row key={item.id} item={item} variant={variant} isSelected={item.id === selectedId} onSelect={selectRow} />
          ))}
        </CategoryGroup>
      ))
    : items.map((item, index) => {
        const row = (
          <Row
            key={item.id}
            item={item}
            variant={variant}
            isSelected={item.id === selectedId}
            onSelect={selectRow}
            showDivider={!isReview && index > 0}
            viewers={viewersById?.[item.id]}
          />
        );
        const action = rowAction?.(item);
        if (!action) return row;
        // Beside the row's button, not inside it: a button cannot hold another control. Over the
        // row's top-right corner, shown on hover or focus (and while its menu is open), always on
        // touch screens where there is no hover.
        return (
          <div key={item.id} className="group/row relative">
            {row}
            <div className="absolute right-2 top-2.5 opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100">
              {action}
            </div>
          </div>
        );
      });

  const body = (
    <>
      {showSkeleton && <ListSkeleton variant={variant} />}
      {isEmpty && (
        <p
          className={cn(
            isReview
              ? 'px-4 py-10 text-center text-sm text-muted-foreground'
              : 'px-2 py-10 text-center text-[12px] text-gray-700',
          )}
        >
          {emptyMessage ?? (isReview ? 'No conversations awaiting review.' : 'No tickets in this queue.')}
        </p>
      )}
      {!showSkeleton && !isEmpty && rows}

      {hasMore && <div ref={sentinelRef} className="h-8" />}
      {isFetchingNextPage && (
        <div className="flex justify-center py-3">
          <Spinner className="text-gray-700" />
        </div>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'flex h-full flex-col border-r border-border',
        isReview ? 'w-full bg-background' : 'w-[280px] shrink-0 bg-white/80',
        className,
      )}
    >
      {/* One group: heading, toolbar and search share a single bottom border. */}
      <div className={cn('shrink-0', !isReview && 'flex flex-col gap-2 border-b border-border pb-3')}>
        <div
          className={cn(
            'flex items-center justify-between',
            isReview ? 'h-16 shrink-0 border-b border-border px-4' : 'h-12 px-3 pt-1',
          )}
        >
          {isReview ? (
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
          ) : (
            // min-w-0: a wide title gives way, so the buttons beside it stay inside the 280px column.
            <div className="flex min-w-0 flex-col gap-1 overflow-hidden">
              <div className="text-[16px] font-semibold leading-[1.1] text-text-body">{title}</div>
              {totalCount !== undefined && (
                <p className="text-[10px] leading-[1.1] text-gray-700">{totalCount} Tickets</p>
              )}
            </div>
          )}

          {headerActions && <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">{headerActions}</div>}

          {onCollapse && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onCollapse}
              className="shrink-0 text-gray-900"
              aria-label="Collapse ticket list"
            >
              <PanelLeft className="size-[14px]" />
            </Button>
          )}
        </div>

        {/* empty:hidden: a toolbar that renders nothing leaves no gap behind. */}
        {toolbar && <div className="px-3 empty:hidden">{toolbar}</div>}

        {search && (
          <div className={isReview ? 'border-b border-border px-4 py-3' : 'px-3'}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <SearchInput
                  value={search.value}
                  onChange={(event) => search.onChange(event.target.value)}
                  placeholder={search.placeholder ?? 'Search by contact'}
                  // The review queue keeps its 16px input; the 280px workspace column gets the compact one.
                  className={cn('w-full', isReview ? 'text-base placeholder:text-base' : 'h-8')}
                />
              </div>
              {searchAction}
            </div>
          </div>
        )}
      </div>

      {isReview ? (
        <ContainedScrollArea>
          <div className="flex min-w-0 flex-col">{body}</div>
        </ContainedScrollArea>
      ) : (
        // The app's thin scrollbar, not Chrome's wide default drawn over the rows' timestamps.
        <div className={cn('custom-scrollbar flex-1 px-1.5', showSkeleton ? 'overflow-hidden' : 'overflow-y-auto')}>{body}</div>
      )}
    </div>
  );
}

function CategoryGroup({
  category,
  count,
  children,
}: {
  category: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Collapsible className="border-b border-border">
      {/* Category header row: chevron on the left, label, count on the right */}
      <CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/50">
        <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
        <span className="flex-1 truncate text-sm font-medium text-foreground">{category}</span>
        <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-violet-100 px-1 text-xs font-medium text-violet-600">
          {count}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}

// Every row re-renders on each poll, so a row only redraws when its own data changes.
const Row = memo(function Row({
  item,
  variant,
  isSelected,
  onSelect,
  showDivider = false,
  viewers,
}: {
  item: TicketListItem<TicketListId>;
  variant: TicketListVariant;
  isSelected: boolean;
  onSelect: (id: TicketListId) => void;
  showDivider?: boolean;
  viewers?: Viewer[];
}) {
  if (variant === 'review') {
    return (
      <button
        type="button"
        onClick={() => onSelect(item.id)}
        className={cn(
          'flex w-full cursor-pointer gap-3 border-b border-border px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-muted/50',
          isSelected && 'bg-muted',
        )}
      >
        <Avatar className="mt-0.5 size-9 shrink-0">
          <AvatarFallback className="text-xs">{initialsOf(item.customerName)}</AvatarFallback>
        </Avatar>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-start justify-between gap-2">
            <TruncatedText text={item.subject} className="min-w-0 text-sm font-semibold text-foreground" />
            <span className="shrink-0 text-xs text-muted-foreground">{relativeTime(item.timestamp)}</span>
          </div>
          {item.customerName && <span className="line-clamp-1 text-xs text-muted-foreground">{item.customerName}</span>}
          {item.preview && <span className="line-clamp-1 text-xs text-muted-foreground">{item.preview}</span>}
          {(item.pendingCount ?? 0) > 1 && (
            <span className="mt-1.5 text-xs text-muted-foreground">{item.pendingCount} pending</span>
          )}
        </div>
      </button>
    );
  }

  return (
    <div className="mt-1">
      {showDivider && <div className="h-px w-full bg-border/60 my-1" />}
      <button
        type="button"
        onClick={() => onSelect(item.id)}
        aria-current={isSelected ? 'true' : undefined}
        className={cn(
          'flex w-full gap-[13px] min-h-[5.25rem] rounded-xl p-2 text-left hover:cursor-pointer',
          // bg-muted alone is a 4% step off white — the open ticket has to be obvious.
          isSelected ? 'bg-gray-200 ring-1 ring-gray-300' : 'hover:bg-muted/70',
        )}
      >
        <Avatar className="size-8 aspect-square shrink-0 rounded-full border border-border bg-white">
          <AvatarFallback className="rounded-full text-[11px] font-medium text-text-quote">
            {initialsOf(item.customerName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-col gap-1 leading-[1.1]">
            <div className="flex items-center justify-between gap-2">
              <TruncatedText
                text={item.subject}
                className={cn('min-w-0 text-[14px] text-text-body', item.unread ? 'font-semibold' : 'font-medium')}
              />
              <span className="flex shrink-0 flex-col items-end gap-1">
                <p className="text-[10px] text-gray-800">{item.timestamp ? compactTimeAgo(item.timestamp) : ''}</p>
                {item.unread && <span className="size-1.5 rounded-full bg-emerald-500" />}
              </span>
            </div>
            <p className="text-[10px] font-medium text-gray-700">
              {item.customerName}
              {item.ownerName !== undefined && (
                <span className="text-gray-600"> · {item.ownerName ?? 'Unassigned'}</span>
              )}
            </p>
            <div className="flex items-center gap-1.5">
              <p
                className={cn(
                  'min-w-0 flex-1 truncate text-[10px]',
                  item.unread ? 'font-semibold text-text-body' : 'text-text-quote',
                )}
              >
                {item.preview}
              </p>
              {viewers && (
                // As tall as the line of text, so people coming and going never move the rows.
                <span
                  className="flex h-[11px] items-center"
                  title={`Also viewing: ${viewers.map((viewer) => viewer.name).join(', ')}`}
                >
                  <ViewerAvatars viewers={viewers} />
                  <span className="sr-only">Also viewing: {viewers.map((viewer) => viewer.name).join(', ')}</span>
                </span>
              )}
            </div>
          </div>
          {item.category && (
            <div className="flex flex-wrap items-center gap-1.5">
              <CategoryChip label={item.category} />
            </div>
          )}
        </div>
      </button>
    </div>
  );
});

function ListSkeleton({ variant }: { variant: TicketListVariant }) {
  if (variant === 'review') {
    return (
      <div className="flex flex-col gap-4 p-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="flex gap-3">
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Same shape as the workspace Row.
  return (
    <>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="mt-1">
          {i > 0 && <div className="h-px w-full bg-border/60 my-1" />}
          <div className="flex min-h-[5.25rem] gap-[13px] p-2">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-2.5 w-8" />
                </div>
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-2.5 w-3/4" />
              </div>
              <Skeleton className="h-[22px] w-28 rounded-[6px]" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
